import type { IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import { SubscriptionProvider } from '@on3/ui-lib/components/Subscription/Provider';
import { ArticleProvider, SiteProvider } from '@on3/ui-lib/index';
import { Adblock } from '@public/components/Analytics/Adblock';
import { Quantcast } from '@public/components/Analytics/Quantcast';
import { Sailthru } from '@public/components/Analytics/Sailthru';
import { Spiny } from '@public/components/Analytics/Spiny';
import { SubscribeHeader } from '@public/components/Header/Subscribe';
import { ModalProvider } from '@public/components/Modals';
import Head from 'next/head';
import { useRouter } from 'next/router';

import styles from './SubscribeLayout.module.scss';

interface ISubscribeLayoutProps extends IGenericPageProps {
  title: string;
  children: React.ReactNode;
  noIndex?: boolean;
  background?: string;
  noPadding?: boolean;
  canonical?: string | null;
}

export const SubscribeLayout = ({
  children,
  title,
  siteData,
  pageType,
  background = '#f5f6f9',
  noIndex = false,
  noPadding = false,
  canonical,
  ...props
}: ISubscribeLayoutProps) => {
  const router = useRouter();

  return (
    <SiteProvider
      currentSite={siteData?.currentSite}
      defaultYears={siteData?.defaultYears}
      menuData={siteData?.menuData}
      pageType={pageType || 'other'}
      secondaryMenuData={siteData?.secondaryMenuData}
      siteUrls={siteData?.siteUrls}
      sportsData={siteData?.sportsData}
      trendingMenuData={siteData?.trendingMenuData}
    >
      <SubscriptionProvider subscriptionData={siteData?.subscriptionData}>
        <ModalProvider>
          <ArticleProvider {...props}>
            <Head>
              {title && (
                <>
                  <title key="title">{title}</title>
                  <meta content={title} key="ogTitle" property="og:title" />
                </>
              )}
              <meta content="website" property="og:type" />
              {canonical && <link href={canonical} rel="canonical" />}
              {canonical && <meta content={canonical} property="og:url" />}
              {noIndex && <meta content="noindex" name="robots" />}
            </Head>
            <Quantcast key={`quantcast-${router.asPath}`} />
            <Spiny key={`spiny-${router.asPath}`} />
            <Sailthru key={`sailthru-${router.asPath}`} />
            <Adblock key={`adblock-${router.asPath}`} />
            <div className={styles.layout}>
              <SubscribeHeader />
              <main>{children}</main>
            </div>
          </ArticleProvider>
        </ModalProvider>
      </SubscriptionProvider>
    </SiteProvider>
  );
};
